"use client";
import type { Document } from "@contentful/rich-text-types";
import { useAlgoliaRecommendations } from "@hooks/useAlgoliaRecommendations";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { AnalyticsListTypes } from "@lib/analytics/analyticsTypes";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ui/components/media-and-icons/image/imagePaths";
import hexRgb from "hex-rgb";
import type { FC } from "react";
import { memo, useEffect, useState } from "react";
import { ProductCarousel } from "../product-carousel/ProductCarousel";
import type { AlgoliaProduct } from "@lib/wrappers/algolia";
import { useLocale } from "next-intl";

type ProductRecommendationsProps = {
    objectIDs: string[];
    headline: string;
    model?:
        | "related-products"
        | "bought-together"
        | "get-trending-global-items"
        | "get-trending-items-for-facet"
        | "get-trending-facets";
    authorizationInvitation?: Document;
};

export const ProductRecommendations: FC<ProductRecommendationsProps> = memo(
    (props): JSX.Element => {
        const locale = useLocale();
        const { recommendationClient, indexName } = useAlgoliaRecommendations();
        const [slideData, setSlideData] = useState([]);
        const [loadHits, setLoadHits] = useState(true);
        const model = props.model ? props.model : "related-products";
        const objectIDs = props.objectIDs;
        const assistiveMessages = {
            "related-products":
                "These are products we found similar to the product you are looking at",
            "bought-together":
                "These are products that have been purchased by other people together with the product you are looking at",
            "get-trending-global-items ": "These products are trending among our customers",
            "get-trending-items-for-facet":
                "These products are trending for the current category among our customers",
            "get-trending-facets": "These categories are trending among our customers",
        };

        useEffect(() => {
            const promises = objectIDs.map((sku) =>
                recommendationClient.getRecommendations([
                    {
                        indexName,
                        objectID: sku,
                        model: model as any,
                        queryParameters: { facetFilters: ["hasStock:true"] },
                    },
                ])
            );

            Promise.all(promises)
                .then((responses) => {
                    const allResults = responses.flatMap(({ results }) =>
                        results[0].hits.map((hit: AlgoliaProduct, index) => ({
                            id: hit.id ?? hit.objectID,
                            productColors: hit.productColors.map((it) => ({
                                priceWithDiscount: it.priceRaw,
                                priceWithoutDiscount: it.listPriceRaw,
                                image: it.image,
                                objectID: it.objectID,
                                colorCode: it.colorCode,
                                imageBackgroundColor: it.imageBackgroundColor,
                                discountPercentage: it.discountPercentage,
                                hasDiscount: it.hasDiscount,
                                hasStock: it.hasStock,
                                lowestPrice: it.lowestPriceRaw,
                                discountPercentageLowestPrice: it.discountPercentageLowestPrice,
                            })),
                            objectID: hit.objectID,
                            queryID: hit.__queryID,
                            index,
                            productName: hit.name,
                            name: hit.name,
                            dominantColor: hit.dominantColor,
                            articleNumber: hit.articleNumber,
                            colorCode: hit.colorCode,
                            image: hit.image || PRODUCT_IMAGE_PLACEHOLDER_URL,
                            hasDiscount: hit.hasDiscount,
                            listPrice: hit.listPrice,
                            price: hit.price,
                            priceWithoutDiscount: hit.listPriceRaw,
                            priceWithDiscount: hit.priceRaw,
                            discountPercentage: hit.discountPercentage,
                            lowestPrice: hit.lowestPriceRaw,
                            discountPercentageLowestPrice: hit.discountPercentageLowestPrice,
                            imageBackgroundColor: hexRgb((hit.backgroundColor ?? "FFF") as string, {
                                format: "css",
                                alpha: 1,
                            }),
                            descriptiveHeading: hit.descriptiveHeading,
                            badges: hit.badges,
                            division: hit.division,
                            collectionType: hit.collectionType,
                            collectionClass: hit.collectionClass,
                            variants: hit.variants,
                        }))
                    );
                    // Deduplicate by objectID
                    const uniqueResults = allResults.filter(
                        (item, index, self) =>
                            index === self.findIndex((t) => t.objectID === item.objectID)
                    );
                    setSlideData(uniqueResults);
                    setLoadHits(false);
                })
                .catch((e) => {
                    Logger.error(ServiceType.WEB, "ProductRecommendations", {
                        tag: LogTag.PRODUCT,
                        message: e,
                    });
                    setLoadHits(false);
                });
        }, [model, setSlideData, indexName, objectIDs, recommendationClient, locale]);

        useEffect(() => {
            slideData?.length &&
                AnalyticsClient.loadItemsList(slideData, {
                    listName: AnalyticsListTypes.recommendations,
                });
        }, [slideData]);

        return (
            <ProductCarousel
                slideData={slideData}
                isLoading={loadHits}
                headline={props.headline}
                assistiveMessage={assistiveMessages[model]}
                carouselType={AnalyticsListTypes.recommendations}
                authorizationInvitation={props.authorizationInvitation}
            />
        );
    }
);

ProductRecommendations.displayName = "ProductRecommendations";

export default ProductRecommendations;
